import { LeftOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Form, Spin } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useReactToPrint } from 'react-to-print'

import {
  useTreatmentServiceGetTreatmentPlanEndpoint,
  useTreatmentServiceGetTreatmentPlanEndpointKey,
  useTreatmentServiceGetTreatmentPlanListEndpointKey,
  useTreatmentServiceUpdateTreatmentPlanEndpoint
} from 'src/api/queries'
import {
  ApiError,
  GetTreatmentPlanResponse,
  TreatmentPlanStatus
} from 'src/api/requests'
import { PrinterIcon } from 'src/assets'
import { LoadingScreen, message, PageBreadcrumbs } from 'src/components'
import {
  CancelModal,
  TreatmentBody,
  TreatmentHeader
} from 'src/features/persons/components/PersonTreatmentsSection/components'
import { usePersonBreadcrumb } from 'src/features/persons/hooks'
import { formatDate, yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

export const PersonTreatmentsDetailsPage = () => {
  const { t, currentLang } = useLocales()
  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const [modalState, setModalState] = useState<{
    type: 'cancel' | 'finish' | null
    treatment?: GetTreatmentPlanResponse
  }>({ type: null })

  const [form] = Form.useForm()

  const { id, personId } = useParams()

  const { data, isLoading } = useTreatmentServiceGetTreatmentPlanEndpoint(
    {
      id: String(id)
    },
    [String(id)],
    { enabled: !!id }
  )

  const { breadcrumbItems, fullName, isLoadingPerson } =
    usePersonBreadcrumb(personId)

  const onBackClick = () => {
    navigate(-1)
  }

  const onCancel = () => {
    setModalState({ type: null })
  }

  const printableRef = useRef<HTMLDivElement>(null)

  const onPrint = useReactToPrint({
    contentRef: printableRef,
    documentTitle: `person-treatment-${id}`
  })

  const { mutate: updateTreatment } =
    useTreatmentServiceUpdateTreatmentPlanEndpoint({
      onSuccess: () => {
        message.success(t('persons.treatment.treatment_updated'))
        queryClient.invalidateQueries({
          queryKey: [useTreatmentServiceGetTreatmentPlanEndpointKey]
        })
        queryClient.invalidateQueries({
          queryKey: [useTreatmentServiceGetTreatmentPlanListEndpointKey]
        })
      },
      onError: (error: ApiError) => formErrorHandler(error.body.errors)
    })

  useEffect(() => {
    if (id && data) {
      form.setFieldsValue({
        person: {
          simeCode: data?.person?.simeCode,
          tarvCode: data?.person?.tarvCode
        },
        created: data?.created ? dayjs(data?.created) : undefined,
        endDate: data?.endDate ? dayjs(data?.endDate) : undefined,
        treatmentPlanId: data?.id,
        treatmentTypeRegimen:
          data?.treatmentTypeRegimen?.translation?.[currentLang?.value],
        treatmentType: data?.treatmentType?.translation?.[currentLang?.value],
        preventionTreatmentContingentTypeId:
          data?.preventionTreatmentContingentType?.translation?.[
            currentLang?.value
          ]
      })
    }
  }, [data, id])

  const isCancelled =
    data?.status === TreatmentPlanStatus.CANCELLED ||
    data?.status === TreatmentPlanStatus.COMPLETED

  if (isLoading || isLoadingPerson) {
    return <LoadingScreen />
  }

  return (
    <Spin spinning={isLoadingPerson}>
      <PageBreadcrumbs title={fullName} items={breadcrumbItems}>
        <Flex vertical gap={24}>
          <Flex justify="space-between">
            <Button
              icon={<LeftOutlined />}
              className="fit-content"
              onClick={onBackClick}
            >
              {t('common.back')}
            </Button>

            <Flex gap={16}>
              {!isCancelled && (
                <Button
                  danger
                  className="fit-content"
                  onClick={() => setModalState({ type: 'cancel' })}
                >
                  {t('treatments.cancel_treatment')}
                </Button>
              )}

              <Button icon={<PrinterIcon />} onClick={() => onPrint()}>
                {t('transfers.print')}
              </Button>
            </Flex>
          </Flex>

          <Form
            form={form}
            layout="vertical"
            onValuesChange={(changedValues) => {
              if (!('treatment_end_date' in changedValues)) return

              const value = form.getFieldValue('treatment_end_date')
              const errors = form.getFieldError('treatment_end_date')

              if (!value || errors.length) return

              updateTreatment({
                id: String(data?.id),
                requestBody: {
                  endDate: formatDate(value, yearMonthDaySlash)
                }
              })
            }}
          >
            <Flex vertical gap={16} ref={printableRef}>
              <TreatmentHeader data={data} />
              <TreatmentBody form={form} data={data} />
            </Flex>
          </Form>
          {modalState.type === 'cancel' && (
            <CancelModal treatment={data} onCancel={onCancel} />
          )}
        </Flex>
      </PageBreadcrumbs>
    </Spin>
  )
}
